import { useClientVD } from '@solinkcorp/agent/hooks/useClientVD';
import { honoFetcher } from '@solinkcorp/agent/lib/honoFetcher';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import type { InvestigationEventKind } from './useInvestigations';

interface RemoveEventParams {
  investigationId: string;
  eventId: string;
}

interface AddEventParams {
  investigationId: string;
  eventId: string;
  siteId: string;
  kind: InvestigationEventKind;
  metadata?: Record<string, unknown>;
}

export interface ReplaceInvestigationEventsByKindParams {
  investigationId: string;
  kind: InvestigationEventKind;
  items: { eventId: string; siteId: string }[];
}

export function useInvestigationEventsMutation() {
  const client = useClientVD();
  const { mutate } = useSWRConfig();

  const addEvent = useCallback(
    async (params: AddEventParams) => {
      return honoFetcher(
        client.investigations[':id'].events.$post({
          param: { id: params.investigationId },
          json: {
            eventId: params.eventId,
            siteId: params.siteId,
            kind: params.kind,
            ...(params.metadata && { metadata: params.metadata }),
          },
        }),
      );
    },
    [client],
  );

  const removeEvent = useCallback(
    async (params: RemoveEventParams) => {
      return honoFetcher(
        client.investigations[':id'].events.$delete({
          param: { id: params.investigationId },
          json: { eventId: params.eventId },
        }),
      );
    },
    [client],
  );

  const replaceEventsByKind = useCallback(
    async (params: ReplaceInvestigationEventsByKindParams) => {
      return honoFetcher(
        client.investigations[':id'].events['by-kind'].$patch({
          param: { id: params.investigationId },
          json: { kind: params.kind, items: params.items },
        }),
      );
    },
    [client],
  );

  const revalidate = useCallback(
    async (investigationId: string) => {
      await mutate(
        (key: unknown) =>
          Array.isArray(key) &&
          (key[0] === 'investigations' ||
            (key[0] === 'investigation-detail' && key[1] === investigationId)),
      );
    },
    [mutate],
  );

  const addEventAndRevalidate = useCallback(
    async (params: AddEventParams) => {
      const result = await addEvent(params);
      await revalidate(params.investigationId);
      return result;
    },
    [addEvent, revalidate],
  );

  const removeEventAndRevalidate = useCallback(
    async (params: RemoveEventParams) => {
      const result = await removeEvent(params);
      await revalidate(params.investigationId);
      return result;
    },
    [removeEvent, revalidate],
  );

  const replaceEventsByKindAndRevalidate = useCallback(
    async (params: ReplaceInvestigationEventsByKindParams) => {
      const result = await replaceEventsByKind(params);
      await revalidate(params.investigationId);
      return result;
    },
    [replaceEventsByKind, revalidate],
  );

  return {
    addEvent,
    addEventAndRevalidate,
    removeEvent,
    removeEventAndRevalidate,
    replaceEventsByKind,
    replaceEventsByKindAndRevalidate,
    revalidate,
  };
}
